import React, {useState} from 'react';
import { createPortal } from 'react-dom';
import './MobilePile.css';
import './WeekdayPills.css';
import { normalizeStatus } from '../utils';
import { parseLocalDate } from '../utils/dateUtils';
import TaskViewModal from '../TaskViewModal';

const weekdays = ['L', 'M', 'X', 'J', 'V'];

function MobilePile({
  tasks,
  setTasks,
  users = [],
  projects = [],
  taskStatusFilter = 'all',
  onTaskStatusFilterChange,
  onCreateTask
}) {

  const today = new Date();
  const [currentWeekStart, setCurrentWeekStart] = useState(getStartOfWeek(today));
  const [currentDayIndex, setCurrentDayIndex] = useState(getInitialDayIndex(today));
  const [selectedTask, setSelectedTask] = useState(null);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);

  function getStartOfWeek(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  }

  function getInitialDayIndex(date) {
    const day = date.getDay();
    return day === 0 ? 0 : day - 1;
  }

  const getDateOfDayIndex = (index) => {
    const date = new Date(currentWeekStart);
    date.setDate(date.getDate() + index);
    return date;
  };

  function isSameDay(dateA, dateB) {
    if (!dateA || !dateB) return false;
    return (
      dateA.getFullYear() === dateB.getFullYear() &&
      dateA.getMonth() === dateB.getMonth() &&
      dateA.getDate() === dateB.getDate()
    );
  }

  const formatDate = (date) => {
    const formatted = new Intl.DateTimeFormat('es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);

    return formatted
      .replace(/ de /g, ' ')
      .replace(/^./, (letter) => letter.toUpperCase());
  };

  const formatShortDate = (date) => {
    return new Intl.DateTimeFormat('es-MX', {
      day: 'numeric'
    }).format(date);
  };

  const formatMonth = (date) => {
    const month = new Intl.DateTimeFormat('es-MX', {
      month: 'long'
    }).format(date);

    return month.charAt(0).toUpperCase() + month.slice(1);
  };

  const formatYear = (date) => {
    return new Intl.DateTimeFormat('es-MX', {
      year: 'numeric'
    }).format(date);
  };

  const handleWeekChange = (direction) => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() + direction * 7);
    setCurrentWeekStart(newStart);
  };

  const handleDateSelect = (event) => {
    const [year, month, day] = event.target.value.split('-').map(Number);
    const selectedDate = new Date(year, month - 1, day);
    const selectedDay = selectedDate.getDay();

    const newWeekStart = getStartOfWeek(selectedDate);
    setCurrentWeekStart(newWeekStart);

    if (selectedDay >= 1 && selectedDay <= 5) {
      setCurrentDayIndex(selectedDay - 1);
    } else {
      setCurrentDayIndex(0);
    }
  };

  const formatInputDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const currentDate = getDateOfDayIndex(currentDayIndex);
  const weekStart = getDateOfDayIndex(0);
  const weekEnd = getDateOfDayIndex(4);

  const tasksOfDay = tasks.filter((task) => {
    if (!task.dueDate) return false;
    const due = parseLocalDate(task.dueDate);
    return isSameDay(due, currentDate);
  });

  const handleToggleComplete = async (task) => {
    const updatedTask = {
      ...task,
      status: task.status === 'completada' ? 'bloqueada' : 'completada',
    };

    setTasks((prev) =>
      prev.map((t) => (t._id === task._id ? updatedTask : t))
    );

    try {
      await fetch(`/api/tasks/${task._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedTask),
      });
    } catch (error) {
      console.error('❌ Error al actualizar tarea:', error);
    }
  };

  return (
    <div className="mobile-pile-container">
      <div className="weekday-row">
        <button
          className="arrow-button"
          onClick={() => handleWeekChange(-1)}
          aria-label="Día anterior"
        >
          ◀
        </button>
        <div className="weekday-pills small">
          {weekdays.map((label, idx) => {
            const isSelected = idx === currentDayIndex;
            return (
              <label
                key={idx}
                className={`pill ${isSelected ? 'selected' : ''}`}
                onClick={() => setCurrentDayIndex(idx)}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  readOnly
                />
                {label}
              </label>
            );
          })}
        </div>
        <button
          className="arrow-button"
          onClick={() => handleWeekChange(1)}
          aria-label="Día siguiente"
        >
          ▶
        </button>
      </div>

      <div className="calendar-controls compact">
        <div className="calendar-left-actions">
          <button
            type="button"
            className="create-task-picker"
            onClick={onCreateTask}
            aria-label="Crear nueva tarea"
            title="Crear nueva tarea"
          >
            <span className="create-task-icon" aria-hidden="true"></span>
          </button>

          <button
            type="button"
            className={`filter-task-picker ${
              taskStatusFilter !== 'all' ? 'active' : ''
            }`}
            onClick={() => {
              setFilterPanelOpen(true);
            }}
            aria-label="Filtrar tareas"
            title="Filtrar tareas"
          >
            <span className="filter-task-icon" aria-hidden="true"></span>
          </button>
        </div>

        <div className="calendar-summary">
          <div className="week-range">
            {formatShortDate(weekStart)} al {formatShortDate(weekEnd)} {formatMonth(weekEnd)} {formatYear(weekEnd)}
          </div>

          <span className="day-label">
            {isSameDay(currentDate, today) && (
              <span className="today-label">Hoy</span>
            )}
            <span>{formatDate(currentDate)}</span>
          </span>
        </div>

        <div className="calendar-picker-wrapper">
          <span
            className="calendar-picker"
            aria-hidden="true"
          ></span>

          <input
            className="calendar-native-input"
            type="date"
            value={formatInputDate(currentDate)}
            onChange={handleDateSelect}
            aria-label="Ir a una fecha"
            title="Ir a una fecha"
          />
        </div>
      </div>

      {filterPanelOpen && createPortal(
        <div
          className="mobile-filter-overlay"
          onClick={() => setFilterPanelOpen(false)}
        >
          <aside
            className="mobile-filter-panel"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mobile-filter-header">
              <strong>Filtrar tareas</strong>

              <button
                type="button"
                className="mobile-filter-close"
                onClick={() => setFilterPanelOpen(false)}
                aria-label="Cerrar filtros"
              >
                ×
              </button>
            </div>

            <strong className="mobile-filter-title">Estado</strong>

            {[
              ['all', 'Todas las tareas'],
              ['completed', 'Terminadas'],
              ['incomplete', 'Incompletas'],
              ['separator', ''],
              ['nueva', 'Nueva'],
              ['trabajando', 'Trabajando'],
              ['bloqueada', 'Bloqueada'],
              ['pendiente', 'Pendiente']
            ].map(([value, label]) =>
              value === 'separator' ? (
                <div
                  key="separator"
                  className="mobile-filter-separator"
                />
              ) : (
                <button
                  type="button"
                  key={value}
                  className={`mobile-filter-value ${
                    taskStatusFilter === value
                      ? `selected selected-${value}`
                      : ''
                  }`}
                  onClick={() => {
                    onTaskStatusFilterChange?.(value);
                    setFilterPanelOpen(false);
                  }}
                >
                  <span>{label}</span>

                  {taskStatusFilter === value && (
                    <span aria-hidden="true">✓</span>
                  )}
                </button>
              )
            )}
          </aside>
        </div>,
        document.body
      )}

      <div className="task-list">
        {tasksOfDay.length === 0 ? (
          <div className="no-tasks-message">No tienes tareas en esta sección</div>
        ) : (
          tasksOfDay.map((task) => (
            <div
              key={task._id}
              className={`task-item ${normalizeStatus(task.status)}`}
              onClick={() => setSelectedTask(task)}
              role="button"
              tabIndex="0"
            >
              <label onClick={(event) => event.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={task.status === 'completada'}
                  onChange={() => handleToggleComplete(task)}
                  className="checkbox"
                />
                <span className="task-title">{task.title?.slice(0, 45)}</span>
              </label>
              {task.notes && (
                <div className="task-notes">
                  {task.notes.slice(0, 189)}
                </div>
              )}

              <span
                className="task-open-guide"
                aria-hidden="true"
              >
                +
              </span>
            </div>
          ))
        )}
      </div>

      {selectedTask && createPortal(
        <TaskViewModal
          task={selectedTask}
          users={users}
          projects={projects}
          onClose={() => setSelectedTask(null)}
          variant="fast-view"
        />,
        document.body
      )}
    </div>
  );
}

export default MobilePile;

